import { ApolloClient, gql } from 'apollo-boost';
import {
  AdminProgramQuestion,
  AdminProgramQuestionFlagInput,
  AdminProgramQuestionsOverview,
  UpdateNpoReportQuestionInput,
} from './types';

const questionFragment = `
  questionId
  text
  name
  section
  sectionStart
  questionType
  required
  helpContent
  reportingType
  nextQuestionId
  tab
  isProgramQuestion
  enabled
  programAnswersCount
`;

export const adminProgramQuestionsOverviewQuery = gql`
  query AdminProgramQuestionsOverview(
    $reportingType: String
    $includeDisabled: Boolean
  ) {
    adminProgramQuestionsOverview(
      reportingType: $reportingType
      includeDisabled: $includeDisabled
    ) {
      total
      programFlagged
      sections {
        section
        total
        programFlagged
        questions { ${questionFragment} }
      }
    }
  }
`;

export const adminSetProgramQuestionFlagMutation = gql`
  mutation AdminSetProgramQuestionFlag(
    $questionId: Int!
    $isProgramQuestion: Boolean!
  ) {
    adminSetProgramQuestionFlag(
      questionId: $questionId
      isProgramQuestion: $isProgramQuestion
    ) {
      success
      question { ${questionFragment} }
    }
  }
`;

export const adminBulkSetProgramQuestionFlagsMutation = gql`
  mutation AdminBulkSetProgramQuestionFlags(
    $items: [AdminProgramQuestionFlagInput!]!
  ) {
    adminBulkSetProgramQuestionFlags(items: $items) {
      success
      updatedCount
      questions { ${questionFragment} }
    }
  }
`;

export const updateNpoReportQuestionMutation = gql`
  mutation UpdateNpoReportQuestion(
    $id: Int!
    $text: String
    $name: String
    $section: String
    $sectionStart: Boolean
    $required: Boolean
    $helpContent: String
    $questionType: String
    $reportingType: String
    $isProgramQuestion: Boolean
    $nextQuestionId: Int
    $tab: String
  ) {
    updateNpoReportQuestion(
      id: $id
      text: $text
      name: $name
      section: $section
      sectionStart: $sectionStart
      required: $required
      helpContent: $helpContent
      questionType: $questionType
      reportingType: $reportingType
      isProgramQuestion: $isProgramQuestion
      nextQuestionId: $nextQuestionId
      tab: $tab
    ) {
      success
      id
    }
  }
`;

/**
 * Superadmin-only program editor mutation. Not exercised by this admin
 * page directly (there's no NPO program editor in manage-accounts yet),
 * but exported so the shared surface lives in one place.
 */
export const adminUpdateProgramMutation = gql`
  mutation AdminUpdateProgram(
    $id: Int!
    $name: String
    $visibility: String
    $status: String
    $managerId: Int
    $accountId: Int
    $logoFileId: Int
    $level1Note: String
    $isCompleted: Boolean
  ) {
    adminUpdateProgram(
      id: $id
      name: $name
      visibility: $visibility
      status: $status
      managerId: $managerId
      accountId: $accountId
      logoFileId: $logoFileId
      level1Note: $level1Note
      isCompleted: $isCompleted
    ) {
      success
      program {
        dbId
        name
        visibility
        status
        managerId
        isCompleted
      }
    }
  }
`;

// --- typed client wrappers -------------------------------------------------

export const fetchAdminProgramQuestionsOverview = (
  client: ApolloClient<object>,
  variables: {
    reportingType?: string | null;
    includeDisabled?: boolean | null;
  } = {},
) =>
  client.query<{
    adminProgramQuestionsOverview: AdminProgramQuestionsOverview;
  }>({
    query: adminProgramQuestionsOverviewQuery,
    variables,
    fetchPolicy: 'network-only',
  });

export const setProgramQuestionFlag = (
  client: ApolloClient<object>,
  questionId: number,
  isProgramQuestion: boolean,
) =>
  client.mutate<{
    adminSetProgramQuestionFlag: {
      success: boolean;
      question: AdminProgramQuestion | null;
    };
  }>({
    mutation: adminSetProgramQuestionFlagMutation,
    variables: { questionId, isProgramQuestion },
    fetchPolicy: 'no-cache',
  });

export const bulkSetProgramQuestionFlags = (
  client: ApolloClient<object>,
  items: AdminProgramQuestionFlagInput[],
) =>
  client.mutate<{
    adminBulkSetProgramQuestionFlags: {
      success: boolean;
      updatedCount: number;
      questions: AdminProgramQuestion[];
    };
  }>({
    mutation: adminBulkSetProgramQuestionFlagsMutation,
    variables: { items },
    fetchPolicy: 'no-cache',
  });

export const updateNpoReportQuestion = (
  client: ApolloClient<object>,
  id: number,
  input: UpdateNpoReportQuestionInput,
) =>
  client.mutate<{
    updateNpoReportQuestion: { success: boolean; id: number | null };
  }>({
    mutation: updateNpoReportQuestionMutation,
    variables: { id, ...input },
    fetchPolicy: 'no-cache',
  });
